import { ICartItemCreate } from "../types";

/**
 * Identifies an add-to-cart request by the columns of the CartItem
 * "Unique Item + Cart + Patient" DB constraint. Two requests sharing this key
 * would either merge into the same row or collide on the constraint, so we must
 * not let them run concurrently.
 */
export const getCartItemAddDedupeKey = (body: ICartItemCreate): string =>
  `${body.cart_id}:${body.item}:${body.patient_id ?? "none"}`;

export type InFlightGuard = {
  /**
   * Reserves `key` and returns `true` if no request for it is in flight.
   * Returns `false` (without reserving) when a matching request is already
   * running, so the caller can drop the duplicate.
   */
  reserve: (key: string) => boolean;
  /** Releases `key` once its request settles, allowing future requests through. */
  release: (key: string) => void;
};

/**
 * Tracks the set of keys with a request currently in flight. Used to drop
 * duplicate concurrent add-to-cart requests synchronously — react-query's
 * `isLoading` only flips on the next render, so a fast double-click (or a
 * programmatic double-add) can otherwise fire two identical POSTs before the
 * triggering button disables.
 */
export const createInFlightGuard = (): InFlightGuard => {
  const inFlightKeys = new Set<string>();
  return {
    reserve: (key) => {
      if (inFlightKeys.has(key)) {
        return false;
      }
      inFlightKeys.add(key);
      return true;
    },
    release: (key) => {
      inFlightKeys.delete(key);
    },
  };
};
